import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useEffect, useRef, useState } from "react";

type DocumentPreviewState =
  | { status: "idle" }
  | { status: "loading"; documentId: string }
  | { status: "ready"; documentId: string; url: string }
  | { status: "error"; documentId: string };

export function useSubmissionDocumentPreview({
  documentId,
  inline,
  onError,
}: {
  documentId: string | null;
  inline?: boolean;
  onError?: (error: unknown) => void;
}) {
  const [state, setState] = useState<DocumentPreviewState>({ status: "idle" });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [getDownloadLink] = useDocumentDownloadLink();

  useEffect(() => {
    let isCurrentDocument = true;

    if (!documentId) {
      setState((current) => (current.status === "idle" ? current : { status: "idle" }));
      return () => {
        isCurrentDocument = false;
      };
    }

    setState({ status: "loading", documentId });
    const variables = inline === undefined ? { id: documentId } : { id: documentId, inline };

    void getDownloadLink({ variables })
      .then(({ data }) => {
        if (!isCurrentDocument) {
          return;
        }

        const url = data?.getDocumentDownloadLink;
        if (url) {
          setState({ status: "ready", documentId, url });
          return;
        }

        setState({ status: "error", documentId });
        onErrorRef.current?.(new Error("No document download link returned"));
      })
      .catch((error: unknown) => {
        if (!isCurrentDocument) {
          return;
        }

        setState({ status: "error", documentId });
        onErrorRef.current?.(error);
      });

    return () => {
      isCurrentDocument = false;
    };
  }, [documentId, getDownloadLink, inline]);

  const isSelectedDocumentState = state.status !== "idle" && state.documentId === documentId;

  return {
    documentUrl: isSelectedDocumentState && state.status === "ready" ? state.url : null,
    isLoading: isSelectedDocumentState && state.status === "loading",
    hasError: isSelectedDocumentState && state.status === "error",
  };
}
